import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import {
  pageTransition,
  staggerContainer,
  staggerItem,
  tapScale,
  tapScaleTransition,
  modalBackdrop,
  modalPanel,
} from '../helpers/animations';
import { ReportListSkeleton } from './skeletons';

function getBankInfo(row) {
  const bank = row?.bank && typeof row.bank === 'object' ? row.bank : null;
  if (!bank) {
    return {
      type: '',
      title: '',
      account: row?.upi_id || row?.bank_id || '',
      holder: '',
      ifsc: '',
    };
  }

  const type = String(bank.type || '').toLowerCase() === 'upi' ? 'upi' : 'bank';
  const upiId = String(bank.upi_id || (type === 'upi' ? bank.account_no : '') || '').trim();
  const accountNo = String(bank.account_no || '').trim();
  const holder = String(bank.name || bank.account_holder || '').trim();
  const bankName = String(bank.bank_name || '').trim();
  const ifsc = String(bank.ifsc_code || '').trim();

  if (type === 'upi') {
    return {
      type: 'upi',
      title: 'UPI',
      account: upiId || accountNo,
      holder,
      ifsc: '',
    };
  }

  const masked = accountNo.length > 4 ? `••••${accountNo.slice(-4)}` : accountNo;

  return {
    type: 'bank',
    title: bankName || 'Bank',
    account: masked,
    holder,
    ifsc,
  };
}

function formatDate(value) {
  return value
    ? new Date(value).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
}

function renderStatusBadge(status) {
  if (status === '1') return <span className="badge badge-success">Success</span>;
  if (status === '2') return <span className="badge badge-danger">Rejected</span>;
  return <span className="badge badge-warning">Pending</span>;
}

/**
 * Shared report list UI.
 * @param {'buy'|'sell'} type
 */
function ExchangeReport({ type }) {
  const isSell = type === 'sell';
  const endpoint = isSell ? 'sell-list' : 'buy-list';
  const title = isSell ? 'Sell report' : 'Buy report';
  const emptyText = isSell
    ? 'No sell withdrawals found for the selected filters.'
    : 'No buy exchanges found for the selected filters.';

  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    fromDate: '',
    toDate: '',
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchList = useCallback(
    async ({ pageToLoad = 1, append = false, currentFilters = filters } = {}) => {
      if (!token) {
        toast.error('Session expired. Please open your access link again.');
        return;
      }

      if ((currentFilters.fromDate && !currentFilters.toDate) || (!currentFilters.fromDate && currentFilters.toDate)) {
        toast.error('Please select both From and To date');
        return;
      }

      const payload = {
        token,
        page_no: pageToLoad,
        limit,
        search: currentFilters.search || '',
        from_date: currentFilters.fromDate && currentFilters.toDate ? currentFilters.fromDate : '',
        to_date: currentFilters.fromDate && currentFilters.toDate ? currentFilters.toDate : '',
        status: currentFilters.status === 'all' ? '' : currentFilters.status,
      };

      setLoading(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/${endpoint}`, payload);
        if (data?.error === false && data?.data) {
          const nextList = Array.isArray(data.data.list) ? data.data.list : [];
          setList((prev) => {
            const merged = append ? [...prev, ...nextList] : nextList;
            const metaTotal = Number(data.data.meta?.total || 0);
            setTotal(metaTotal);
            setHasMore(merged.length < metaTotal);
            return merged;
          });
        } else if (data?.error === 'session expired') {
          toast.error('Session expired. Please open your access link again.');
        } else if (data?.error) {
          toast.error(typeof data.error === 'string' ? data.error : `Failed to fetch ${type} list`);
        }
      } catch {
        // api helper shows error toast
      } finally {
        setLoading(false);
      }
    },
    [endpoint, filters, limit, token, type]
  );

  useEffect(() => {
    setList([]);
    setTotal(0);
    setHasMore(true);
    setPage(1);
    setSelected(null);
    fetchList({ pageToLoad: 1, append: false, currentFilters: filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, type]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || !hasMore) return;
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 200;
      if (scrollPosition >= threshold) {
        const nextPage = page + 1;
        const maxPages = Math.max(1, Math.ceil(total / limit));
        if (nextPage <= maxPages) {
          setPage(nextPage);
          fetchList({ pageToLoad: nextPage, append: true });
        } else {
          setHasMore(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchList, hasMore, limit, loading, page, total]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setList([]);
    setHasMore(true);
    fetchList({ pageToLoad: 1, append: false });
  };

  const handleResetFilters = () => {
    const reset = {
      status: 'all',
      search: '',
      fromDate: '',
      toDate: '',
    };
    setFilters(reset);
    setPage(1);
    setList([]);
    setHasMore(true);
    fetchList({ pageToLoad: 1, append: false, currentFilters: reset });
  };

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        {title}
      </motion.h1>
      <motion.p className="buy-merchant-back" variants={staggerItem} initial="initial" animate="animate">
        <Link to={tokenPath(token, 'account')}>Back to Account</Link>
      </motion.p>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <button
          type="button"
          className="filters-toggle"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <span>Filters</span>
          <span className="filters-toggle-icon">{showFilters ? '▴' : '▾'}</span>
        </button>
        {showFilters && (
          <motion.form
            className="form filters-form"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            onSubmit={handleApplyFilters}
          >
            <motion.div className="form-group" variants={staggerItem}>
              <label>Status</label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All</option>
                <option value="0">Pending</option>
                <option value="1">Success</option>
                <option value="2">Rejected</option>
              </select>
            </motion.div>

            <motion.div className="form-group" variants={staggerItem}>
              <label>Search</label>
              <input
                type="text"
                placeholder="UTR, user ID, exchange ID"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </motion.div>

            <motion.div className="form-group" variants={staggerItem}>
              <label>Date range</label>
              <div className="date-range-row">
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
                />
                <span className="date-range-separator">to</span>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
                />
              </div>
              <p className="form-hint">Leave empty to see all dates.</p>
            </motion.div>

            <motion.div className="filters-actions" variants={staggerItem}>
              <motion.button
                type="button"
                className="btn ghost btn-sm"
                whileTap={tapScale}
                transition={tapScaleTransition}
                onClick={handleResetFilters}
                disabled={loading}
              >
                Reset
              </motion.button>
              <motion.button
                type="submit"
                className="btn primary btn-sm"
                whileTap={tapScale}
                transition={tapScaleTransition}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply'}
              </motion.button>
            </motion.div>
          </motion.form>
        )}
      </motion.div>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        {loading && list.length === 0 ? (
          <ReportListSkeleton />
        ) : list.length === 0 && !loading ? (
          <p className="form-hint" style={{ textAlign: 'center', margin: 0 }}>
            {emptyText}
          </p>
        ) : list.length > 0 ? (
          <ul className="exchange-list">
            {list.map((row) => {
              const bankInfo = isSell ? getBankInfo(row) : null;
              return (
                <li
                  key={row.exchange_id}
                  className="exchange-item"
                  onClick={() => setSelected(row)}
                >
                  <div className="exchange-item-main">
                    <div className="exchange-item-amount">
                      <span className="exchange-item-label">Amount</span>
                      <span className="exchange-item-value">₹ {Number(row.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="exchange-item-amount">
                      <span className="exchange-item-label">USDT</span>
                      <span className="exchange-item-value">{Number(row.usdt || 0).toFixed(2)}</span>
                    </div>
                    <div className="exchange-item-status">{renderStatusBadge(String(row.status))}</div>
                  </div>
                  <div className="exchange-item-meta">
                    {isSell ? (
                      <>
                        <span>
                          {bankInfo?.type === 'upi'
                            ? `UPI: ${bankInfo.account || 'N/A'}`
                            : `Bank: ${[bankInfo?.title, bankInfo?.account].filter(Boolean).join(' · ') || 'N/A'}`}
                        </span>
                        <span>Name: {bankInfo?.holder || 'N/A'}</span>
                      </>
                    ) : (
                      <>
                        <span>UTR: {row.utr_no || row.utr || 'N/A'}</span>
                        <span>UPI: {row.upi_id || 'N/A'}</span>
                      </>
                    )}
                  </div>
                  <div className="exchange-item-meta small">
                    <span>{formatDate(row.create_date)}</span>
                    {String(row.status) === '2' && row.reject_reason && (
                      <span className="exchange-reject-reason">Reason: {row.reject_reason}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {loading && list.length > 0 && (
          <p className="form-hint" style={{ marginTop: 8, textAlign: 'center' }}>
            Loading...
          </p>
        )}
      </motion.div>

      <AnimatePresence>
        {selected && (
          <motion.div className="modal-backdrop" {...modalBackdrop} onClick={() => setSelected(null)}>
            <motion.div className="modal" {...modalPanel} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="card-title">{isSell ? 'Sell details' : 'Buy details'}</h2>
                <p className="card-subtitle">Full summary of this exchange.</p>
              </div>
              <div className="modal-body">
                <div className="modal-summary-row">
                  <div className="modal-summary-chip">
                    <span className="modal-summary-label">Amount</span>
                    <span className="modal-summary-value">₹ {Number(selected.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="modal-summary-chip">
                    <span className="modal-summary-label">USDT</span>
                    <span className="modal-summary-value">{Number(selected.usdt || 0).toFixed(2)} USDT</span>
                  </div>
                </div>

                <div className="modal-info-card">
                  <div className="modal-info-row">
                    <span className="modal-info-label">Status</span>
                    <span className="modal-info-value">{renderStatusBadge(String(selected.status))}</span>
                  </div>
                  <div className="modal-info-row">
                    <span className="modal-info-label">Exchange ID</span>
                    <span className="modal-info-value">{selected.exchange_id || 'N/A'}</span>
                  </div>

                  {isSell ? (
                    (() => {
                      const bankInfo = getBankInfo(selected);
                      return (
                        <>
                          <div className="modal-info-row">
                            <span className="modal-info-label">Type</span>
                            <span className="modal-info-value">
                              {bankInfo.type === 'upi' ? 'UPI' : 'Bank'}
                            </span>
                          </div>
                          {bankInfo.type === 'upi' ? (
                            <>
                              <div className="modal-info-row">
                                <span className="modal-info-label">UPI ID</span>
                                <span className="modal-info-value">{bankInfo.account || 'N/A'}</span>
                              </div>
                              <div className="modal-info-row">
                                <span className="modal-info-label">Name</span>
                                <span className="modal-info-value">{bankInfo.holder || 'N/A'}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="modal-info-row">
                                <span className="modal-info-label">Bank name</span>
                                <span className="modal-info-value">{bankInfo.title || 'N/A'}</span>
                              </div>
                              <div className="modal-info-row">
                                <span className="modal-info-label">Account no.</span>
                                <span className="modal-info-value">
                                  {selected.bank?.account_no || bankInfo.account || 'N/A'}
                                </span>
                              </div>
                              <div className="modal-info-row">
                                <span className="modal-info-label">Account holder</span>
                                <span className="modal-info-value">{bankInfo.holder || 'N/A'}</span>
                              </div>
                              <div className="modal-info-row">
                                <span className="modal-info-label">IFSC</span>
                                <span className="modal-info-value">{bankInfo.ifsc || 'N/A'}</span>
                              </div>
                            </>
                          )}
                          {selected.exchange_from || selected.exchange_to ? (
                            <>
                              <div className="modal-info-row">
                                <span className="modal-info-label">From</span>
                                <span className="modal-info-value">{selected.exchange_from || 'N/A'}</span>
                              </div>
                              <div className="modal-info-row">
                                <span className="modal-info-label">To</span>
                                <span className="modal-info-value">{selected.exchange_to || 'N/A'}</span>
                              </div>
                            </>
                          ) : null}
                        </>
                      );
                    })()
                  ) : (
                    <>
                      <div className="modal-info-row">
                        <span className="modal-info-label">UTR no.</span>
                        <span className="modal-info-value">{selected.utr_no || selected.utr || 'N/A'}</span>
                      </div>
                      <div className="modal-info-row">
                        <span className="modal-info-label">UPI ID</span>
                        <span className="modal-info-value">{selected.upi_id || 'N/A'}</span>
                      </div>
                      <div className="modal-info-row">
                        <span className="modal-info-label">User ID</span>
                        <span className="modal-info-value">{selected.user_id || 'N/A'}</span>
                      </div>
                      {selected.file && (
                        <div className="modal-info-row">
                          <span className="modal-info-label">Screenshot</span>
                          <span className="modal-info-value">
                            <a href={selected.file} target="_blank" rel="noreferrer">
                              View file
                            </a>
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="modal-info-row">
                    <span className="modal-info-label">Created at</span>
                    <span className="modal-info-value">{formatDate(selected.create_date)}</span>
                  </div>
                </div>

                {String(selected.status) === '2' && selected.reject_reason && (
                  <div className="modal-warning-card">
                    <div className="modal-warning-title">
                      <span>Rejected reason</span>
                    </div>
                    <p className="form-hint" style={{ marginTop: 2 }}>
                      {selected.reject_reason}
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <motion.button
                  type="button"
                  className="btn ghost btn-sm"
                  whileTap={tapScale}
                  transition={tapScaleTransition}
                  onClick={() => setSelected(null)}
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default ExchangeReport;
